<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicInput from '~/components/form-elements/BasicInput.vue'
import ConfirmDiscountSelection from '~/components/modal-windows/ConfirmDiscountSelection.vue'
import constants from '~/constants'
import DiscountType from '~/enums/discount-type'
import Mask from '~/enums/mask'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import { useCartStore } from '~/store/cart'

const maskaOptions = { number: Mask.getMaskaOptions(Mask.Number) }
const messages = {
  // Promo code.
  promoCodeFieldCannotBeEmpty: 'Поле «Промокод» не может быть пустым.',

  // Points.
  failedToApplyPoints: 'Не удалось применить баллы iTAB Плюс.',
  maximumNumberOfPointsForOrderHasBeenExceeded: 'Превышено максимальное количество баллов iTAB Плюс на заказ.',
  pointsFieldCannotBeEmpty: 'Поле «Баллы iTAB Плюс для списания» не может быть пустым.',
  pointsToSpendMustBePositiveIntegers: 'Баллы iTAB Плюс для списания должны быть положительными целыми числами.',
  pointsWillBeRedeemed: 'Баллы iTAB Плюс будут списаны.',

  // Supermins points.
  failedToApplySuperminsPoints: 'Не удалось применить баллы Supermins.',
  maximumNumberOfSuperminsPointsForOrderHasBeenExceeded: 'Превышено максимальное количество баллов Supermins на заказ.',
  superminsPointsFieldCannotBeEmpty: 'Поле «Баллы Supermins для списания» не может быть пустым.',
  superminsPointsToSpendMustBePositiveIntegers: 'Баллы Supermins для списания должны быть положительными целыми числами.',
  superminsPointsWillBeRedeemed: 'Баллы Supermins будут списаны.',

  // Certificate.
  failedToApplyCertificate: 'Не удалось применить сертификат.',
  certificateFieldCannotBeEmpty: 'Поле «Код активации сертификата» не может быть пустым.',
  certificateIsApplied: 'Сертификат применён.'
}

const { $toast } = useNuxtApp()
const cartStore = useCartStore()

const discountType = ref(DiscountType.PromoCode)
const showCertificateBlock = ref(false)

const discountTypes = computed(
  () => DiscountType
    .sortedMembers
    .filter((x: DiscountType): boolean => {
      switch (x) {
        case discountType.value:
          return false
        case DiscountType.Points:
          return cartStore.maxPointsForOrder !== null && cartStore.maxPointsForOrder > 0
        case DiscountType.SuperminsPoints:
          return cartStore.maxSuperminsPointsForOrder !== null && cartStore.maxSuperminsPointsForOrder > 0
        default:
          return true
      }
    })
)

async function applyCertificate (): Promise<void> {
  const { failedToApplyCertificate, certificateFieldCannotBeEmpty, certificateIsApplied } = messages

  if (!cartStore.form.certificateName) {
    $toast.error(certificateFieldCannotBeEmpty)

    return
  }

  cartStore.isCertificateApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  if (cartStore.isDataLoadingError) {
    $toast.error(failedToApplyCertificate)
  } else if (cartStore.errorDetail) {
    $toast.error(cartStore.errorDetail)
  } else if (cartStore.errorCode) {
    $toast.error(ApiErrorHandler.getMessage(cartStore.errorCode, failedToApplyCertificate))
  } else {
    cartStore.isCertificateApplied = true

    $toast.success(certificateIsApplied)
  }

  cartStore.isDataLoading2 = false
  cartStore.isCertificateApplying = false
}

async function applyPoints (isSupermins = false): Promise<void> {
  const { pointsToSpend, superminsPointsToSpend } = cartStore.form
  const {
    // Points.
    failedToApplyPoints,
    maximumNumberOfPointsForOrderHasBeenExceeded,
    pointsFieldCannotBeEmpty,
    pointsToSpendMustBePositiveIntegers,
    pointsWillBeRedeemed,

    // Supermins points.
    failedToApplySuperminsPoints,
    maximumNumberOfSuperminsPointsForOrderHasBeenExceeded,
    superminsPointsFieldCannotBeEmpty,
    superminsPointsToSpendMustBePositiveIntegers,
    superminsPointsWillBeRedeemed
  } = messages

  let errors

  if (isSupermins) {
    errors = [
      { condition: superminsPointsToSpend === null, message: superminsPointsFieldCannotBeEmpty },
      {
        condition: !Number.isInteger(Number(superminsPointsToSpend)) || Number(superminsPointsToSpend) <= 0,
        message: superminsPointsToSpendMustBePositiveIntegers
      },
      {
        condition: superminsPointsToSpend > cartStore.maxSuperminsPointsForOrder,
        message: maximumNumberOfSuperminsPointsForOrderHasBeenExceeded
      }
    ]
  } else {
    errors = [
      { condition: pointsToSpend === null, message: pointsFieldCannotBeEmpty },
      {
        condition: !Number.isInteger(Number(pointsToSpend)) || Number(pointsToSpend) <= 0,
        message: pointsToSpendMustBePositiveIntegers
      },
      { condition: pointsToSpend > cartStore.maxPointsForOrder, message: maximumNumberOfPointsForOrderHasBeenExceeded }
    ]
  }

  for (const { condition, message } of errors) {
    if (condition) {
      $toast.error(message)

      return
    }
  }

  cartStore.isPointsApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  if (cartStore.isDataLoadingError) {
    $toast.error(isSupermins ? failedToApplySuperminsPoints : failedToApplyPoints)
  } else if (cartStore.errorDetail) {
    $toast.error(cartStore.errorDetail)
  } else if (cartStore.errorCode) {
    $toast.error(
      ApiErrorHandler.getMessage(
        cartStore.errorCode,
        isSupermins ? failedToApplySuperminsPoints : failedToApplyPoints
      )
    )
  } else {
    if (isSupermins) {
      cartStore.areSuperminsPointsApplied = true
    } else {
      cartStore.arePointsApplied = true
    }

    $toast.success(isSupermins ? superminsPointsWillBeRedeemed : pointsWillBeRedeemed)
  }

  cartStore.isDataLoading2 = false
  cartStore.isPointsApplying = false
}

async function applyPromoCode (): Promise<void> {
  const { promoCodeFieldCannotBeEmpty } = messages

  if (!cartStore.form.promoCodeName) {
    $toast.error(promoCodeFieldCannotBeEmpty)

    return
  }

  cartStore.isPromoCodeApplying = true
  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
  cartStore.isPromoCodeApplying = false
}

async function cancelCertificate (): Promise<void> {
  cartStore.isCertificateApplied = false

  cartStore.form.certificateName = null

  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function cancelPoints (isSupermins = false): Promise<void> {
  if (isSupermins) {
    cartStore.form.superminsPointsToSpend = cartStore.maxSuperminsPointsForOrder

    cartStore.areSuperminsPointsApplied = false
  } else {
    cartStore.form.pointsToSpend = cartStore.maxPointsForOrder

    cartStore.arePointsApplied = false
  }

  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function cancelPromoCode (): Promise<void> {
  cartStore.isPromoCodeApplied = false

  clearPromoCodeField()

  cartStore.isDataLoading2 = true

  await cartStore.fetchCartProducts()

  cartStore.isDataLoading2 = false
}

async function changeDiscountType (x: DiscountType): Promise<void> {
  const { pointsToSpend, promoCodeName, superminsPointsToSpend } = cartStore.form
  const { Points, PromoCode, SuperminsPoints } = DiscountType

  if (
    (discountType.value === PromoCode && promoCodeName && cartStore.isPromoCodeApplied)
    || (discountType.value === Points && pointsToSpend !== 0 && cartStore.arePointsApplied)
    || (discountType.value === SuperminsPoints && superminsPointsToSpend !== 0 && cartStore.areSuperminsPointsApplied)
  ) {
    await useModal({
      component: ConfirmDiscountSelection,
      attrs: {
        currentDiscountType: discountType.value,
        newDiscountType: x,
        onConfirmDiscountSelection: confirmDiscountSelection
      }
    }).open()

    return
  }

  if (discountType.value === PromoCode) {
    cartStore.form.promoCodeName = null
  }

  discountType.value = x
}

function clearPromoCodeField (): void {
  cartStore.form.promoCodeName = null

  useCookie('promoCode').value = null
}

async function confirmDiscountSelection (x: DiscountType): Promise<void> {
  const { pointsToSpend, promoCodeName, superminsPointsToSpend } = cartStore.form
  const { Points, PromoCode, SuperminsPoints } = DiscountType

  switch (discountType.value) {
    case Points:
      if (pointsToSpend !== 0 && cartStore.arePointsApplied) {
        await cancelPoints()
      }

      break
    case PromoCode:
      if (promoCodeName && cartStore.isPromoCodeApplied) {
        await cancelPromoCode()
      }

      break
    case SuperminsPoints:
      if (superminsPointsToSpend !== 0 && cartStore.areSuperminsPointsApplied) {
        await cancelPoints(true)
      }

      break
    default:
      break
  }

  if (discountType.value === PromoCode) {
    cartStore.form.promoCodeName = null
  }

  discountType.value = x
}
</script>

<template>
  <BlockWithBorder>
    <div class="h4">
      Примените скидку
    </div>

    <div v-show="discountType === DiscountType.PromoCode">
      <template v-if="cartStore.isPromoCodeApplied">
        <Notification class="mb-2" is-small status="success">
          Промокод применён: <code>{{ cartStore.form.promoCodeName }}</code>.
        </Notification>

        <BasicButton color="red" is-small title="Удалить промокод" @click="cancelPromoCode" />
      </template>
      <template v-else>
        <BasicInput v-model="cartStore.form.promoCodeName" label-text="Промокод" @enter="applyPromoCode" />

        <div v-if="cartStore.form.promoCodeName" class="mt-2">
          <BasicButton class="mb-2" color="green" is-small title="Применить промокод" @click="applyPromoCode" />
          <BasicButton color="body-background" is-small title="Очистить поле" @click="clearPromoCodeField" />
        </div>
      </template>
    </div>

    <div v-show="discountType === DiscountType.Points">
      <template v-if="cartStore.arePointsApplied">
        <Notification class="mb-6" is-small status="info">
          На&nbsp;этот заказ вы&nbsp;потратите {{ helpers.formatPrice(cartStore.form.pointsToSpend) }}&nbsp;{{ helpers.getDeclension(cartStore.form.pointsToSpend, constants.wordForms.point) }} iTAB Плюс.
        </Notification>

        <BasicButton color="body-background" is-small title="Отменить списание баллов" @click="cancelPoints" />
      </template>
      <template v-else>
        <Notification class="mb-6" is-small status="info">
          У&nbsp;вас {{ helpers.formatPrice(cartStore.userTotalPoints) }}&nbsp;{{ helpers.getDeclension(cartStore.userTotalPoints, constants.wordForms.point) }} iTAB Плюс. На&nbsp;этот заказ можно потратить не&nbsp;более&nbsp;{{ helpers.formatPrice(cartStore.maxPointsForOrder) }}.
        </Notification>

        <BasicInput
          v-model.number="cartStore.form.pointsToSpend"
          v-maska="maskaOptions.number"
          class="mb-4"
          label-text="Баллы iTAB Плюс для списания"
          @enter="applyPoints"
        />

        <BasicButton color="green" is-small title="Потратить баллы" @click="applyPoints" />
      </template>
    </div>

    <div v-show="discountType === DiscountType.SuperminsPoints">
      <template v-if="cartStore.areSuperminsPointsApplied">
        <Notification class="mb-6" is-small status="info">
          На&nbsp;этот заказ вы&nbsp;потратите {{ helpers.formatPrice(cartStore.form.superminsPointsToSpend) }}&nbsp;{{ helpers.getDeclension(cartStore.form.superminsPointsToSpend, constants.wordForms.point) }} Supermins.
        </Notification>

        <BasicButton color="body-background" is-small title="Отменить списание баллов" @click="cancelPoints(true)" />
      </template>
      <template v-else>
        <Notification class="mb-6" is-small status="info">
          У&nbsp;вас {{ helpers.formatPrice(cartStore.userTotalSuperminsPoints) }}&nbsp;{{ helpers.getDeclension(cartStore.userTotalSuperminsPoints, constants.wordForms.point) }} Supermins. На&nbsp;этот заказ можно потратить не&nbsp;более&nbsp;{{ helpers.formatPrice(cartStore.maxSuperminsPointsForOrder) }}.
        </Notification>

        <BasicInput
          v-model.number="cartStore.form.superminsPointsToSpend"
          v-maska="maskaOptions.number"
          class="mb-4"
          label-text="Баллы Supermins для списания"
          @enter="applyPoints(true)"
        />

        <BasicButton color="green" is-small title="Потратить баллы" @click="applyPoints(true)" />
      </template>
    </div>

    <template v-if="discountTypes.length">
      <p class="mb-2 mt-4">
        Или:
      </p>

      <ul>
        <li v-for="x in discountTypes" :key="x">
          <a href="#" @click.prevent="changeDiscountType(x)">
            <template v-if="x === DiscountType.PromoCode">Применить промокод</template>
            <template v-else-if="x === DiscountType.Points">
              Потратить баллы iTAB Плюс (не&nbsp;более&nbsp;{{ helpers.formatPrice(cartStore.maxPointsForOrder) }})
            </template>
            <template v-else-if="x === DiscountType.SuperminsPoints">
              Потратить баллы Supermins (не&nbsp;более&nbsp;{{ helpers.formatPrice(cartStore.maxSuperminsPointsForOrder) }})
            </template>
          </a>
        </li>
      </ul>
    </template>

    <hr class="my-4">

    <div v-if="showCertificateBlock">
      <template v-if="cartStore.isCertificateApplied">
        <Notification class="mb-2" is-small status="info">
          Сертификат применён: <code>{{ cartStore.form.certificateName }}</code>.
        </Notification>

        <BasicButton color="red" is-small title="Удалить сертификат" @click="cancelCertificate" />
      </template>
      <template v-else>
        <BasicInput
          v-model="cartStore.form.certificateName"
          class="mb-4"
          label-text="Код активации сертификата"
          @enter="applyCertificate"
        />

        <BasicButton
          v-if="cartStore.form.certificateName"
          color="green"
          is-small
          title="Применить сертификат"
          @click="applyCertificate"
        />
      </template>
    </div>
    <div v-else>
      <a href="#" @click.prevent="showCertificateBlock = !showCertificateBlock">Применить сертификат</a>
    </div>
  </BlockWithBorder>
</template>
